<template>
  <div class="flex flex-1 flex-col">
    <div class="fixed-section xs:sticky xs:z-20 xs:top-[52px] sm:top-[61px] bg-bg-base pt-4">
      <model-search-form @search="handleSearch" :keyword="state.currentSearch"/>
      <div class="flex items-center gap-x-4 my-4">
        <button @click="switchTab('new_purchase')" 
                class="h-8 py-1 pl-4 pr-1 font-medium hover:bg-gray-400 text-14px rounded-full"
                :class="{ ' bg-gray-400 text-white': activeTab === 'new_purchase' }">
          {{ $t('profiles.libraries.new_purchase') }} <span class="inline-block h-6 ml-1 px-2 py-0.5 rounded-full bg-gray-500 ">{{ state.counts.new_purchase }}</span>
        </button>
        <button @click="switchTab('all')" 
                class="py-1 pl-4 pr-1 font-medium hover:bg-gray-400 text-14px rounded-full"
                :class="{ ' bg-gray-400 text-white': activeTab === 'all' }">
          {{ $t('profiles.libraries.all_items') }} <span class="inline-block h-6 ml-1 px-2 py-0.5 rounded-full bg-gray-500">{{ state.counts.all }}</span>
        </button>
      </div>
    </div>

    <div class="flex flex-1 text-14px">
      <div v-if="state.initialLoading" class="flex flex-col flex-1 items-center justify-center">
        <p class="text-16px font-medium text-center">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>
      <div v-else-if="currentUser && items.length === 0 && !state.loading" class="flex flex-col flex-1 items-center justify-center">        
        <img src="../../images/library.svg" class="block w-8 mb-2" />
        <p class="text-16px font-medium text-center">
          {{ $t('profiles.libraries.no_purchase') }}
        </p>
      </div>

      <div v-else class="w-full">
        <div class="model-container">
          <div class='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2'>
            <template v-for="variant in items" :key="variant.id">
              <div class='post_variant p-4 bg-grey-4 rounded-8px'>
                <div class="flex">
                  <div class="flex-1 text-16px font-medium truncate" :title="variant.post.title">
                    {{ variant.post.title }}
                  </div>
                  <div class='variant-more relative'>
                    <input :id="`variant-${variant.id}-more`" type='radio' name='variant-more' class='hidden'>
                    <label :for="`variant-${variant.id}-more`" class='cursor-pointer' @click.prevent="moreBtnClick(variant)">
                      <span class='icon more-icon'></span>
                    </label>

                    <div class='more-content absolute'>
                      <button class='w-full flex items-center' @click.prevent='reportPost(variant)'>
                        <span class='icon report-icon mr-2'></span>
                        <span class='min-w-[32px]'>{{ $t('comment_form.report') }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <a :href="variant.post.path" class="relative block w-full rounded-8px overflow-hidden">
                  <div class="image-block aspect-square no-mask no-shadow">
                    <img v-lazy="variant.post.cover_image_src" :alt="variant.post.title" class="image" />
                  </div>
                  <span class="image-mask aspect-square"></span>

                  <div v-if="variant.phrozen_verified"
                      class="flex items-center justify-center w-10 h-4 font-medium absolute top-2 left-2 text-12px bg-[#E60044] rounded-[4px] z-1">
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-0.5">
                      <path d="M5.99941 9.57694C8.22979 9.57694 10.0379 7.76885 10.0379 5.53847C10.0379 3.30808 8.22979 1.5 5.99941 1.5C3.76902 1.5 1.96094 3.30808 1.96094 5.53847C1.96094 7.76885 3.76902 9.57694 5.99941 9.57694Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5.9982 7.15363C6.89035 7.15363 7.61359 6.43039 7.61359 5.53824C7.61359 4.64609 6.89035 3.92285 5.9982 3.92285C5.10605 3.92285 4.38281 4.64609 4.38281 5.53824C4.38281 6.43039 5.10605 7.15363 5.9982 7.15363Z" fill="white"/>
                      <path d="M8.15536 11.9996L6.00119 9.8457L3.84766 11.9996" stroke="white" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    </svg>
                    PV
                  </div>

                  <div v-if='!variant.current_review' class='review-block'>
                    <div class="text-14 font-medium">
                      {{ $t('profiles.libraries.how_was_print') }}
                    </div>
                    <div class="stars flex gap-x-1">
                      <template v-for="rating in [1, 2, 3, 4, 5]" :key="rating">
                        <label class='cursor-pointer'
                          @click.prevent="ratingClick(variant.post.id, rating)"
                          @mouseenter="ratingEnter(rating)"
                          @mouseleave="ratingLeave"
                        >
                          <template v-if="state.ratingStarHover >= rating">
                            <span class="icon star-hover"></span>
                          </template>
                          <template v-else>
                            <span class="icon star-empty"></span>
                          </template>
                        </label>
                      </template>
                    </div>
                  </div>
                </a>

                <div class="my-2 text-14px font-medium">
                  {{ variant.name }}
                </div>
                <select :name="'post_variant_' + variant.id + '_file_groups'" class="default-input-klass mb-2" data-uniform-select="true">
                  <option v-if="displaySelectAll()" value="all">
                    All
                  </option>
                  <option v-for="file_group in variant.file_groups" :value="file_group.id">
                    {{ file_group.name }}
                  </option>
                </select>
                <button class='w-full btn btn-primary' @click="handleDownload(variant)">
                  {{ $t('buttons.download') }}
                </button>
              </div>
            </template>
          </div>
          <p v-if="state.loading" class="text-16px flex justify-center items-center h-12">
            <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
          </p>
        </div>
      </div>
    </div>
  </div>

  <report-post-modal v-if='reportState.openModal' :reportReason='reportState.reportReason'></report-post-modal>
</template>

<script>

import { defineComponent, ref, reactive, computed, onBeforeMount, onMounted, onUnmounted, onUpdated, nextTick} from "vue";
import { reportState } from "../stores/postState";
import debounce from 'lodash/debounce';

export default defineComponent({
  setup(props) {
    const currentUser = ref(null);
    const activeTab = ref('new_purchase');
    const scrollListener = ref(null);

    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 12,
      currentSearch: '',
      ratingStarHover: 0,
      counts: { all: 0, new_purchase: 0 },
    });

    const stateData = reactive({
      all: { items: [] },
      new_purchase: { items: [] },
    });

    const pageInfo = reactive({
      all: { page: 0, hasMore: true },
      new_purchase: { page: 0, hasMore: true },
    });

    const items = computed(() => stateData[activeTab.value].items);

    const fetchCurrentUser = async () => {
      try {
        const response = await fetch('/api/v1/profile/current_user', {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          currentUser.value = await response.json();
        } else {
          throw new Error('Failed to fetch current user');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    const fetchItemData = async (tab, page, perPage) => {
      if (!pageInfo[tab].hasMore || state.loading) return;

      state.loading = true;

      const url = '/api/v1/profile/purchased_variants';
      const params = new URLSearchParams({
        page: page + 1,
        per_page: perPage,
        kw: state.currentSearch,
        filter: tab
      });

      try {
        const response = await fetch(`${url}?${params}`);

        if (response.ok) {
          const data = await response.json();
          const newItems = data.data;

          stateData[tab].items.push(...newItems);
          state.counts = data.counts;

          if (activeTab.value === null) {
            activeTab.value = data.counts.new_purchase > 0 ? 'new_purchase' : 'all';
          }

          pageInfo[tab].page++;
          pageInfo[tab].hasMore = pageInfo[tab].page < data.pagination.total_pages;
        }
      } catch (error) {
        console.error('Error fetching model data:', error);
        pageInfo[tab].hasMore = false;
      } finally {
        state.loading = false;
      }
    };

    const switchTab = (tabName) => {
      if (activeTab.value === tabName) return;

      activeTab.value = tabName;
      scrollToTop();

      if (stateData[tabName].items.length === 0 && pageInfo[tabName].hasMore) {
        fetchItemData(tabName, 0, state.itemsCount);
      }
    };

    const downloadItemData = async (post_id, variant_id, file_group_id) => {
      const url = `/posts/${post_id}/download`;
      const params = new URLSearchParams({
        post_variant_id: variant_id,
        file_group_id: file_group_id
      });

      const link = document.createElement('a');
      link.href = `${url}?${params}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleSearch = async (keyword) => {
      state.initialLoading = true;
      state.currentSearch = keyword;
      
      Object.keys(stateData).forEach(key => {
        stateData[key].items = [];
        pageInfo[key].page = 0;
        pageInfo[key].hasMore = true;
      });

      await fetchItemData(activeTab.value, 0, state.itemsCount);
      scrollToTop();
    };

    const handleDownload = async (variant) => {
      const select = document.querySelector(`select[name='post_variant_${variant.id}_file_groups']`)

      if (select.value == 'all') {
        const file_group_ids = Array.from(select.options).map(option => option.value);
        file_group_ids.shift()

        file_group_ids.forEach((file_group_id, idx) => {
          setTimeout(() => {
            downloadItemData(variant.post.id, variant.id, file_group_id);
          }, idx * 3000);
        });
      } else {
        downloadItemData(variant.post.id, variant.id, select.value)
      }

      if (variant.is_downloaded === false) {
        state.counts.new_purchase--;
        stateData.new_purchase.items = stateData.new_purchase.items.filter(item => item.id !== variant.id);
      }
      variant.is_downloaded = true;

      if(trackingEvent) {
        trackingEvent.post_download({
          id: variant.post.id,
          title: variant.post.title,
        });
      }
    };

    const moreBtnClick = (variant) => {
      const btn = document.getElementById(`variant-${variant.id}-more`)

      if (btn.checked){
        btn.checked = false
      } else {
        btn.checked = true
      }
    };

    const reportPost = (variant) => {
      document.getElementById(`variant-${variant.id}-more`).checked = false

      reportState.openModal = true
      reportState.targetId = variant.post.id
      reportState.reportKey = null
      reportState.reportReason = null
    };

    const ratingEnter = (value) => {
      state.ratingStarHover = value;
    };

    const ratingLeave = () => {
      state.ratingStarHover = 0;
    };

    const ratingClick = (post_id, stars) => {
      window.location.href = `/posts/${post_id}?#new-review-${stars}`
    };

    const displaySelectAll = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone']
      const tabletKeywords = ['ipad', 'android 3', 'android 4', 'android 5']

      const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword))
      const isTablet = tabletKeywords.some(keyword => userAgent.includes(keyword))

      return !(isMobile || isTablet || window.innerWidth < 1024)
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };

    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };

    const handleScroll = debounce(() => {
      const currentTabInfo = pageInfo[activeTab.value];
      if (!currentTabInfo.hasMore || state.loading) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 300) {
        fetchItemData(activeTab.value, currentTabInfo.page, state.itemsCount);
      }
    }, 100);

    onBeforeMount(() => {
      const kw = new URL(window.location).searchParams.get('kw')
      if (kw) {
        state.currentSearch = new String(kw)
      }
    });

    onMounted(async () => {
      try {
        await fetchCurrentUser();
        if (currentUser.value) {
          // Fetch initial data for the 'all' tab
          await fetchItemData('all', 0, state.itemsCount);

          // After fetching initial data, set the active tab based on counts
          if (state.counts.new_purchase > 0) {
            activeTab.value = 'new_purchase';
            // If switching to new_purchase and its data is not yet loaded, fetch it
            if (stateData.new_purchase.items.length === 0 && pageInfo.new_purchase.hasMore) {
              await fetchItemData('new_purchase', 0, state.itemsCount);
            }
          } else {
            activeTab.value = 'all';
          }
          state.initialLoading = false;
          addScrollListener();
        } else {
          console.error('Failed to fetch current user');
          state.initialLoading = false;
        }
      } catch (error) {
        console.error('Error in onMounted:', error);
        state.initialLoading = false;
      }
    });

    onUnmounted(() => {
      removeScrollListener();
    });

    onUpdated(async () => {
      window.initUniformSelect()
    })

    return {
      currentUser,
      state,
      items,
      reportState,
      pageInfo,
      activeTab,
      switchTab,
      handleSearch,
      handleDownload,
      moreBtnClick,
      reportPost,
      ratingEnter,
      ratingLeave,
      ratingClick,
      displaySelectAll
    };
  }
});
</script>